import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
} from "react";
import { FolderOpenIcon, PlusIcon, UploadIcon } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { ChatUploadModal } from "./ChatUploadModal";
import { LibrarySelectionModal } from "./LibrarySelectionModal";
import type { IAttachmentOption } from "@/api/attachments";
import { toAuiAttachment } from "@/components/shared/user-message-attachment.utils";
import { registerAttachmentFileType } from "@/components/shared/attachment-file-type-registry";
import { registerAttachmentClient } from "@/pages/frontend/chat/utils/attachment-client-registry";
import { useChatFileDrop } from "@/pages/frontend/chat/contexts/ChatFileDropContext";
import { useAui, useAuiState } from "@assistant-ui/react";
import { DirectDocumentBudgetProvider } from "@/components/shared/direct-document-budget";
import { stagedCountedDocumentIds } from "./composer-staged-documents";
import { useDirectDocumentBudgetInvalidation } from "./useDirectDocumentBudgetInvalidation";

/**
 * Dropdown menu for the Composer's attachment button with two options:
 * - "Upload from computer" → ChatUploadModal (file upload via S3)
 * - "Select from document library" → LibrarySelectionModal (existing docs)
 *
 * Selected attachments are added to the assistant-ui composer so they are
 * included on the sent user message and available to the adapter. On the chat
 * page, files dropped anywhere on the page open ChatUploadModal pre-seeded.
 *
 * This is also the single owner of the direct-document budget. It is the one
 * component above BOTH pickers, so it is the only place that can see the
 * combined staged set: validating inside `ChatUploadModal` alone would let a
 * user upload to the cap and then add more from the library, because neither
 * picker can see the other's staging.
 *
 * Owning it here does not merge the two evaluations. The upload path is
 * count-based (its PDFs have no ids yet) and the library path is dedup-aware
 * (its ids may already be attached, and re-selecting one is free); both
 * evaluators reach the pickers through the provider.
 */
export const ComposerAttachmentMenu: FC = () => {
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadInitialFiles, setUploadInitialFiles] = useState<File[]>();
    const [libraryModalOpen, setLibraryModalOpen] = useState(false);
    const aui = useAui();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const fileDrop = useChatFileDrop();

    const openUpload = useCallback((files?: File[]) => {
        setUploadInitialFiles(files);
        setUploadModalOpen(true);
    }, []);

    useEffect(
        () => fileDrop?.registerDropHandler(openUpload),
        [fileDrop, openUpload]
    );

    // The shared Modal disables focus restore, and a drop-opened modal has no
    // trigger to return to. Focusing after the close commits keeps the
    // modal's focus trap from pulling focus back in.
    const wasUploadModalOpen = useRef(uploadModalOpen);
    useEffect(() => {
        if (wasUploadModalOpen.current && !uploadModalOpen) {
            triggerRef.current
                ?.closest("form")
                ?.querySelector("textarea")
                ?.focus();
        }
        wasUploadModalOpen.current = uploadModalOpen;
    }, [uploadModalOpen]);

    // Undefined on a brand-new thread. That is the new-thread case, not a
    // missing value to guard against: nothing is attached yet, so no budget
    // request is issued and the eligible set is empty.
    const remoteId = useAuiState((s) => {
        const id = s.threads.mainThreadId;
        return s.threads.threadItems.find((t) => t.id === id)?.remoteId;
    });
    const composerAttachments = useAuiState((s) => s.composer.attachments);
    const stagedIds = useMemo(
        () => stagedCountedDocumentIds(composerAttachments),
        [composerAttachments]
    );

    const addToComposer = (attachments: IAttachmentOption[]) => {
        const composer = aui.thread().composer();
        for (const a of attachments) {
            const auiAttachment = toAuiAttachment(a);
            // Register before addAttachment: the add triggers the pill
            // re-render that reads the registries (cross-client warning +
            // spreadsheet-vs-PDF styling).
            registerAttachmentClient(auiAttachment.id, a.client_id);
            registerAttachmentFileType(auiAttachment.id, a.file_type);
            composer.addAttachment(auiAttachment);
        }
    };

    const handleUploadConfirm = (attachments: IAttachmentOption[]) => {
        addToComposer(attachments);
        setUploadModalOpen(false);
    };

    const handleLibraryConfirm = (attachments: IAttachmentOption[]) => {
        addToComposer(attachments);
    };

    return (
        <DirectDocumentBudgetProvider
            conversationId={remoteId}
            stagedIds={stagedIds}
            enabled={uploadModalOpen || libraryModalOpen}
        >
            <BudgetInvalidationOnPersist />
            <DropdownMenu>
                <DropdownMenuTrigger
                    ref={triggerRef}
                    render={
                        <TooltipIconButton
                            tooltip="Attach files"
                            side="bottom"
                            variant="ghost"
                            size="icon"
                            className="aui-composer-add-attachment hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30 size-8 rounded-full p-1 text-xs font-semibold"
                            aria-label="Attach files"
                        />
                    }
                >
                    <PlusIcon className="size-5 stroke-[1.5px]" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    className="min-w-56"
                >
                    <DropdownMenuItem onClick={() => openUpload()}>
                        <UploadIcon className="mr-2 size-4" />
                        Upload from computer
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setLibraryModalOpen(true)}>
                        <FolderOpenIcon className="mr-2 size-4" />
                        Select from document library
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ChatUploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onConfirm={handleUploadConfirm}
                initialFiles={uploadInitialFiles}
            />

            <LibrarySelectionModal
                open={libraryModalOpen}
                onClose={() => setLibraryModalOpen(false)}
                onConfirm={handleLibraryConfirm}
            />
        </DirectDocumentBudgetProvider>
    );
};

/**
 * Refetch the budget once a turn settles, which is when staged attachments
 * have actually persisted. Staging must never invalidate — the staged set is
 * already accounted for locally, and refetching on every attach would race the
 * user's next selection.
 */
const BudgetInvalidationOnPersist: FC = () => {
    const isRunning = useAuiState((s) => s.thread.isRunning);
    const wasRunning = useRef(isRunning);
    const { invalidate } = useDirectDocumentBudgetInvalidation();

    useEffect(() => {
        if (wasRunning.current && !isRunning) {
            invalidate();
        }
        wasRunning.current = isRunning;
    }, [isRunning, invalidate]);

    return null;
};
